import { useCallback, useMemo, useState } from 'react'
import { DoubleSide } from 'three'
import shallow from 'zustand/shallow'
import { useTheme } from '@mui/material'
import { useHiddenElements } from '@editor/hooks'
import { ADD_PLAN_IMAGE_CARDS_SELECTION_KEY } from '@editor/hooks/usePlanImageCardTool'
import { ADD_PLAN_TABLES_SELECTION_KEY } from '@editor/hooks/usePlanTableTool'
import { ImmutableVector3, PointShapesMesh } from '@modugen/ui-toolkit'
import { OpacityOption } from '@modugen/ui-toolkit/dist/scene/types/opacity'
import { ThreeEvent } from '@react-three/fiber'
import { useTriggerContextMenu } from '@ui/hooks'
import { useAppStore } from '@stores'
import {
  useControlStore,
  useElementSelectionStore,
  useModelStore,
  useSceneStore,
  useTapelineStore,
} from '@editorStores'
import { useModelClickListeners, useMouseMovedSinceMouseDown } from '@editorHooks'
import { getPolygonNormal } from '../utils'

type ShapeRenderMode = {
  bodyGeometry: boolean
  outline: boolean
}

type ShapeElement = {
  id: string
  points: [number, number, number][]
  openings: [number, number, number][][]
  elementType: ElementTypes
}

interface BatchedShapesMeshProps {
  /** Raw model elements of a single shape group (walls, slabs, …). */
  elements: ShapeObject[]
  /**
   * Opaque-state opacity for this group, mirroring ModelMesh.getTypeMaterial
   * (1 for walls, 0.7 for slabs/roof slabs, 0.5 for vertical slabs).
   */
  baseOpacity: number
  /** Uniform z offset applied to every point (vertical slabs get a small lift). */
  zOffset?: number
  /** Punch openings out as holes. Only walls carry openings. */
  includeOpenings?: boolean
  /** polygonOffsetFactor to avoid z-fighting between coplanar groups. */
  polygonOffsetFactor?: number
}

// Mirror the legacy BasicMesh, which stops propagation of pointer events on the
// element nearest to the camera: without this, r3f delivers pointer moves to
// every intersected batched mesh, so elements behind other elements get hovered
// too. Stopping the move on the nearest mesh also makes r3f send pointerout to
// newly occluded meshes, keeping their hover state consistent.
const stopPointerMovePropagation = (event: ThreeEvent<PointerEvent>) => event.stopPropagation()

/**
 * Batched shape rendering: draws a whole group of shape-like elements (walls,
 * slabs, roof slabs, vertical slabs, vertical roof slabs) in a single
 * `PointShapesMesh` (one batched draw call per transparency bucket), instead of
 * the per-element extruded `ShapeMesh` used in `ModelMesh`. Elements become flat
 * (zero-thickness) polygons.
 *
 * Gated behind the `batchedModelRenderingV2` app-store flag. While it is on,
 * `ModelMesh` skips its own rendering of these types (see its `skipBatchedShapes`
 * prop) so the two do not draw on top of each other. Element overlays (load
 * arrows, indicators, labels) still come from `ModelMesh`.
 */
const BatchedShapesMesh = ({
  elements: modelElements,
  baseOpacity,
  zOffset,
  includeOpenings = false,
  polygonOffsetFactor,
}: BatchedShapesMeshProps) => {
  const { scenePalette } = useTheme()

  const translucent = useModelStore(state => state.translucent)
  const { visibilityByType: transparencyVisibilityByType, guidToTransparent } = useModelStore(
    state => state.transparencyVisibilityModel,
    shallow,
  )
  const transparentOpacity = useAppStore(state => state.transparentOpacityV2 ?? 0.1)
  const sceneGuidToColor = useSceneStore(state => state.guidToColor)

  const hiddenElements = useHiddenElements()

  const selectedStandAloneIds = useElementSelectionStore(state => state.selectedStandAloneIds)
  const highlightedIds = useElementSelectionStore(state => state.highlightedIds)

  // Suppress hover/click/context-menu while the tapeline tool or a plan-table/
  // image-card placement mode is active, matching the `noPointerInteractions`
  // condition SceneControlled passes to the legacy ModelMesh.
  const isTapelineActive = useTapelineStore(state => state.isActive)
  const selectionModeKey = useControlStore(state => state.selectionModeKey)
  const isPlacementMode =
    selectionModeKey === ADD_PLAN_TABLES_SELECTION_KEY ||
    selectionModeKey === ADD_PLAN_IMAGE_CARDS_SELECTION_KEY

  const [hoveredGuid, setHoveredGuid] = useState<string | null>(null)

  const elements = useMemo<ShapeElement[]>(() => {
    return modelElements
      .filter(element => element.shape.points.length >= 3)
      .map(element => ({
        id: element.guid,
        elementType: element.element_type as ElementTypes,
        points: element.shape.points.map(p => [p.x, p.y, p.z] as [number, number, number]),
        openings:
          includeOpenings && element.openings
            ? element.openings
                .filter(opening => !opening.is_local)
                .map(opening =>
                  opening.shape.points.map(p => [p.x, p.y, p.z] as [number, number, number]),
                )
            : [],
      }))
  }, [modelElements, includeOpenings])

  const baseColorForType = useCallback(
    (type: ElementTypes): string => {
      switch (type) {
        case 'outer_walls':
          return scenePalette.elements3d.externalWalls as string
        case 'inner_walls':
          return scenePalette.elements3d.internalWalls as string
        case 'slabs':
          return scenePalette.elements3d.slabs as string
        case 'roof_slabs':
          return scenePalette.elements3d.roof_slabs as string
        case 'vertical_slabs':
          return scenePalette.elements3d.vertical_slabs as string
        case 'vertical_roof_slabs':
          return scenePalette.elements3d.vertical_roof_slabs as string
        default:
          return scenePalette.elements3d.walls as string
      }
    },
    [scenePalette.elements3d],
  )

  const guidToColor = useMemo(() => {
    return elements.reduce((acc: Record<string, string>, el) => {
      // Mirror the legacy ShapeMesh: the sceneStore guidToColor override
      // (problem view, utilization, storey coloring, …) wins over highlight
      // and type colors, for every element type.
      const override = sceneGuidToColor[el.id]
      if (override) {
        acc[el.id] = override
      } else if (highlightedIds.has(el.id)) {
        acc[el.id] = scenePalette.highlightElements as string
      } else {
        acc[el.id] = baseColorForType(el.elementType)
      }
      return acc
    }, {})
  }, [elements, highlightedIds, sceneGuidToColor, scenePalette.highlightElements, baseColorForType])

  const isTransparent = useCallback(
    (guid: string, elementType: ElementTypes) => {
      if (!translucent) return false
      // Priority: guidToTransparent has higher priority than visibilityByType
      const forGuid = guidToTransparent[guid]
      if (forGuid !== undefined) return forGuid
      return transparencyVisibilityByType[elementType] || false
    },
    [translucent, guidToTransparent, transparencyVisibilityByType],
  )

  const opacityOptionValues = useMemo<Record<OpacityOption, number>>(
    () => ({ transparent: transparentOpacity, notTransparent: baseOpacity }),
    [transparentOpacity, baseOpacity],
  )

  const guidToOpacity = useMemo<Record<string, OpacityOption>>(() => {
    return elements.reduce((acc, el) => {
      acc[el.id] = isTransparent(el.id, el.elementType) ? 'transparent' : 'notTransparent'
      return acc
    }, {} as Record<string, OpacityOption>)
  }, [elements, isTransparent])

  const guidToRenderMode = useMemo<Record<string, ShapeRenderMode>>(() => {
    return elements.reduce((acc, el) => {
      // Mirror ModelMesh: outlines only show while the element is not translucent
      const opaque = !isTransparent(el.id, el.elementType)
      acc[el.id] = { bodyGeometry: true, outline: opaque }
      return acc
    }, {} as Record<string, ShapeRenderMode>)
  }, [elements, isTransparent])

  const guidToOutlinesColor = useMemo(() => {
    return elements.reduce((acc: Record<string, string>, el) => {
      const emphasized = selectedStandAloneIds.has(el.id) || el.id === hoveredGuid
      if (emphasized) {
        acc[el.id] = scenePalette.highlightDark as string
      } else {
        // Mirror ModelMesh.getOutlinesColor: slabs and roof slabs get a dark outline
        acc[el.id] =
          el.elementType === 'slabs' || el.elementType === 'roof_slabs'
            ? (scenePalette.outlinesDark as string)
            : (scenePalette.outlines as string)
      }
      return acc
    }, {})
  }, [
    elements,
    selectedStandAloneIds,
    hoveredGuid,
    scenePalette.highlightDark,
    scenePalette.outlines,
    scenePalette.outlinesDark,
  ])

  const guidToHidden = useMemo(() => {
    return elements.reduce((acc: Record<string, boolean>, el) => {
      acc[el.id] = hiddenElements.has(el.id)
      return acc
    }, {})
  }, [elements, hiddenElements])

  const meshBasicMaterialProps = useMemo(() => {
    // Do not force `transparent` here: PointShapesMesh sets it per opacity
    // bucket (opacity < 1). Forcing it would move the opacity-1 bucket (walls)
    // into the transparent render pass, where whole-mesh distance sorting can
    // draw slabs before the walls behind them, making them look extra
    // transparent (blended against the background instead of the wall).
    const base = { side: DoubleSide }
    if (polygonOffsetFactor === undefined) return base
    return { ...base, polygonOffset: true, polygonOffsetFactor, polygonOffsetUnit: 1 }
  }, [polygonOffsetFactor])

  const guidToType = useMemo(() => {
    return elements.reduce((acc: Record<string, ElementTypes>, el) => {
      acc[el.id] = el.elementType
      return acc
    }, {})
  }, [elements])

  // Clip-plane per element, mirroring ModelMesh: plane normal from the element
  // polygon and a point on it. Uses the original (un-offset) points.
  const guidToClipPlane = useMemo(() => {
    return modelElements.reduce(
      (acc: Record<string, { normal: ImmutableVector3; point: ImmutableVector3 }>, element) => {
        const points = element.shape.points
        if (points.length < 3) return acc
        acc[element.guid] = { normal: getPolygonNormal(points), point: points[0] }
        return acc
      },
      {},
    )
  }, [modelElements])

  const { openContextMenu } = useTriggerContextMenu()
  const { mouseMoved } = useMouseMovedSinceMouseDown()

  const emitModelClick = useModelClickListeners()

  const handleClick = useCallback(
    (guid: string, event: ThreeEvent<MouseEvent>) => {
      // Bridge the batched click into the V1 click-listener system by
      // synthesizing an event that mimics the per-element ShapeMesh (name +
      // userData.elementType). Every page's existing selection logic reads
      // exactly those fields, so single and multi (shift) selection work
      // unchanged without any per-page wiring.
      event.stopPropagation()

      const syntheticEvent = {
        ...event,
        object: {
          name: guid,
          userData: { elementType: guidToType[guid] },
          visible: true,
        },
      } as unknown as ThreeEvent<MouseEvent>

      emitModelClick(syntheticEvent)
    },
    [emitModelClick, guidToType],
  )

  const handlePointerOver = useCallback((guid: string) => {
    setHoveredGuid(guid)
  }, [])

  const handlePointerOut = useCallback((guid: string) => {
    setHoveredGuid(prev => (prev === guid ? null : prev))
  }, [])

  const handleContextMenu = useCallback(
    (guid: string, event: ThreeEvent<MouseEvent>) => {
      if (mouseMoved.current) return

      event.stopPropagation()

      const clipPlane = guidToClipPlane[guid]

      openContextMenu(
        event.point,
        guid,
        {
          hide: { guid },
          properties: { guid },
          ...(clipPlane
            ? { 'clip-plane': { normal: clipPlane.normal, point: clipPlane.point } }
            : {}),
        },
        {},
      )
    },
    [mouseMoved, openContextMenu, guidToClipPlane],
  )

  if (elements.length === 0) return null

  return (
    <group onPointerMove={stopPointerMovePropagation}>
      <PointShapesMesh
        elements={elements}
        guidToColor={guidToColor}
        guidToOutlinesColor={guidToOutlinesColor}
        guidToHidden={guidToHidden}
        guidToOpacity={guidToOpacity}
        opacityOptionValues={opacityOptionValues}
        guidToRenderMode={guidToRenderMode}
        handlePointerOver={handlePointerOver}
        handlePointerOut={handlePointerOut}
        handleClick={handleClick}
        handleContextMenu={handleContextMenu}
        meshBasicMaterialProps={meshBasicMaterialProps}
        outlineThickness={1}
        zOffset={zOffset}
        raycastingEnabled={!isTapelineActive && !isPlacementMode}
      />
    </group>
  )
}

export default BatchedShapesMesh
